<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Document, Packer, Paragraph } from "docx";
import { saveAs } from "file-saver";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
  TableCellsIcon,
  DocumentIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShareIcon,
} from "@heroicons/vue/24/outline";
import { api } from "../../api/Api";
import { isNumeric, formatNumber } from "../../utils/CommonFunctions";
import { useScreenSize } from "../../utils/useScreenSize";
import Toast from "../../services/Toast";
import DateInput from '../ui/form/DateInput.vue';
import Buttons from '../ui/form/Buttons.vue';
import { Filter } from "../../types/report";
import FilterSelectListInput from "../ui/form/FilterSelectListInput.vue";
import SearchSelectMultiItems from "../ui/form/SearchSelectMultiItems.vue";

const { isMobile } = useScreenSize();

interface ReportRow {
  id: number;
  hasChild: boolean;
  [key: string]: any;
}

const props = defineProps<{
  reportId: string;
  show: boolean;
}>();

const title = ref("Report Viewer");

const filters = ref<Filter[]>([]);
const reportData = ref<ReportRow[]>([]);
const columns = ref<{ field: string; header: string }[]>([]);

const itemsPerPage = ref(25);

const currentPage = ref(0);
const selectedRow = ref<ReportRow | null>(null);
const showModal = ref(false);
const isFullscreen = ref(false);

const totalPages = computed(() =>
  Math.ceil(reportData.value.length / itemsPerPage.value)
);

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return reportData.value.slice(start, end);
});

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const exportToExcel = () => {
  // Create worksheet with proper data types
  const ws: XLSX.WorkSheet = {};
  const range = {
    s: { c: 0, r: 0 },
    e: { c: columns.value.length - 1, r: reportData.value.length },
  };

  // Add headers
  columns.value.forEach((col, colIdx) => {
    const cell: XLSX.CellObject = {
      v: col.header,
      t: "s", // string type for headers
    };
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
    ws[cellRef] = cell;
  });

  // Add data with proper types
  reportData.value.forEach((row, rowIdx) => {
    columns.value.forEach((col, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
      const value = row[col.field];

      let cell: XLSX.CellObject;

      if (typeof value === "number" || isNumeric(value)) {
        cell = { v: value, t: "n" }; // number type
      } else if (typeof value === "boolean") {
        cell = { v: value, t: "b" }; // boolean type
      } else if (value instanceof Date) {
        cell = { v: value, t: "d" }; // date type
      } else {
        cell = { v: value, t: "s" }; // string type
      }

      ws[cellRef] = cell;
    });
  });

  ws["!ref"] = XLSX.utils.encode_range(range);

  // Create workbook and export
  const wb = XLSX.utils.book_new();
  let fileName = title.value;
  if (fileName.length > 30) {
    fileName = fileName.substring(0, 30);
  }
  XLSX.utils.book_append_sheet(wb, ws, fileName);
  // Use only the first 30 characters of the title if it exceeds 30
  
  XLSX.writeFile(wb, title.value + ".xlsx");
};

const exportToPDF = async (forExport: boolean) => {
  // Set orientation to landscape
  const doc = new jsPDF({ orientation: "landscape" });
  const headers = columns.value.map((col) => col.header);
  const data = reportData.value.map((row) =>
    columns.value.map((col) => {
      const value = row[col.field];
      if (isNumeric(value)) {
        return Number(value).toFixed(2);
      }
      return String(value);
    })
  );

  (doc as any).autoTable({
    head: [headers],
    body: data,
    margin: { top: 20 },
    styles: { fontSize: 8 },
    headStyles: { fillColor: [76, 175, 80] },
  });

  if (forExport) {
    if (navigator.share) {
      try {
        const pdfBlob = doc.output("blob");
        const file = new File([pdfBlob], title.value + ".pdf", {
          type: "application/pdf",
        });

        await navigator.share({
          title: "Faseyha POS",
          text: title.value + " Report",
          files: [file],
        });

        //console.log("PDF shared successfully!");
      } catch (error) {
        Toast.show("Could not share", "warning");
      }
    }
  } else {
    doc.save(title.value + ".pdf");
  }
};

const exportToWord = async () => {
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            text: "Report",
            heading: "Heading1",
          }),
          ...reportData.value.map(
            (row) =>
              new Paragraph({
                text: columns.value
                  .map((col) => `${col.header}: ${row[col.field]}`)
                  .join(", "),
              })
          ),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, title.value + ".docx");
};

const handleRowDoubleClick = (row: ReportRow) => {
  if (row.hasChild) {
    selectedRow.value = row;
    showModal.value = true;
    emit("row-expand", row);
  }
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

const emit = defineEmits<{
  (e: "row-expand", row: ReportRow): void;
  (e: "close"): void;
}>();

const getReport = async (new_rec: Boolean) => {
  currentPage.value = 0;
  const response = await api.post("clients/reports/get-report-data", {
    id: props.reportId,
    filters: filters.value,
    new: new_rec,
  });
  if (response.success == true) {
    reportData.value = response.data.data;
    if (new_rec) {
      filters.value = response.data.filters || [];
    }
    console.log('report fiutlers', filters.value);
    title.value = response.data.title;
    if (reportData.value && reportData.value.length > 0) {
      columns.value = Object.keys(reportData.value[0])
      .filter((key) => key !== "hasChild") // Exclude hasChild from display
      .map((field) => ({
        field,
        header:
          field.charAt(0).toUpperCase() +
          field
            .slice(1)
            .replace(/([A-Z])/g, " $1")
            .trim()
            .replace("_", " "), // Convert camelCase to Title Case
      }));
    }
    currentPage.value = 1;
  }
};

watch(
  () => props.reportId,
  () => {
    if (props.show && props.reportId) {
      getReport(true);
    }
  }
);

watch(
  () => isMobile,
  () => {
    if (isMobile.value) {
      isFullscreen.value = true;
    }
  }
);

watch(
  () => props.show,
  () => {
    if (props.show && isMobile.value) {
      isFullscreen.value = true;
    }
    filters.value = [];
  }
);

onMounted(() => {
  if (isMobile.value) {
    isFullscreen.value = true;
  }
  filters.value = [];  
});

const isNumericColumn = (field: any) => {
  if (field.toLowerCase().includes("date") || field.toLowerCase().includes("time") || field.toLowerCase().includes("code") || field.toLowerCase() == 'cost') {
    return false; // Exclude date/time fields from numeric check
  }
  return paginatedData.value.some((row) => isNumeric(row[field]));
};

const getColumnTotal = (field: any) => {
  return paginatedData.value.reduce((sum, row) => {
    return sum + (isNumeric(row[field]) ? parseFloat(row[field]) : 0);
  }, 0);
};

</script>

<template>
  <div
    v-if="show"
    :class="['report-viewer-container', isFullscreen ? 'fullscreen' : 'modal']"
  >
    <div class="report-viewer" :class="{ 'fullscreen-content': isFullscreen }">
      <!-- Header -->
      <div class="report-header">
        <h2>{{ title }}</h2>
        <div class="header-actions">
          <!-- Export buttons -->
          <template v-if="!isMobile">
            <button
              @click="exportToExcel"
              class="action-button"
              title="Export to Excel"
            >
              <TableCellsIcon class="icon" />
            </button>
            <button
              @click="exportToPDF(false)"
              class="action-button"
              title="Export to PDF"
            >
              <DocumentIcon class="icon" />
            </button>
            <button
              @click="exportToWord"
              class="action-button"
              title="Export to Word"
            >
              <DocumentTextIcon class="icon" />
            </button>
            <div class="divider"></div>
          </template>
          <button
            @click="exportToPDF(true)"
            class="action-button"
            title="Share"
          >
            <ShareIcon class="icon" />
          </button>
          <button
            @click="toggleFullscreen"
            class="action-button"
            :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
          >
            <component
              :is="isFullscreen ? ArrowsPointingInIcon : ArrowsPointingOutIcon"
              class="icon"
            />
          </button>
          <button
            v-if="!isFullscreen || isMobile"
            @click="emit('close')"
            class="action-button"
            title="Close"
          >
            <XMarkIcon class="icon" />
          </button>
        </div>
      </div>

      <div class="report-header">
        <div class="header-actions">
          <div class="md:grid md:grid-cols-5 gap-2">
            <div
                v-for="(filter, idx) in filters"
              :key="'filter-' + idx"
            >
             <DateInput
              v-model="filters[idx].value"
              v-if="filter.inputType === 'date'"
              :label="filter.caption"
              class="col-span-1"
              />
            <FilterSelectListInput
              v-model="filters[idx].value"
              v-if="filter.inputType === 'single-select'"
              :filter="filter"
              :end-point="''"
              :label="filter.caption"
              class="col-span-1"
              />
              <SearchSelectMultiItems
              v-model="filters[idx].value"
              v-if="filter.inputType === 'multi-select'"
              :filter="filter"
              :table="filters[idx].dbTable"
              :end-point="''"
              :label="filter.caption"
              class="col-span-1"
              />
            </div>
            
             <div class="flex items-end h-full">
             <Buttons
              caption="Preview Report"
              icon="RefreshCcwDot"
              class="col-span-1 bg-green-600 dark:bg-green-400 hover:bg-green-800 dark:hover:bg-green-600"
              @click="getReport(false)" />
             </div>
          </div>
        </div>
      </div>

      <!-- Report table -->
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.field">
                {{ column.header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in paginatedData"
              :key="row.id"
              @dblclick="handleRowDoubleClick(row)"
              :class="{ expandable: row.hasChild }"
            >
              <template v-for="column in columns" :key="column.field">
                <td class="text-right" v-if="isNumeric(row[column.field])">
                  {{ formatNumber(row[column.field]) }}
                </td>
                <td v-else>
                  {{ row[column.field] }}
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <template v-for="column in columns" :key="column.field">
                <td class="text-right" v-if="isNumericColumn(column.field)">
                  <strong>{{
                    formatNumber(getColumnTotal(column.field))
                  }}</strong>
                </td>
                <td v-else></td>
              </template>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Pagination controls -->
      <div class="pagination">
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          class="pagination-button"
        >
          <ChevronLeftIcon class="icon" />
          Previous
        </button>
        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }} ({{
            reportData.length
          }}
          records)
        </span>
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="pagination-button"
        >
          Next
          <ChevronRightIcon class="icon" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.report-viewer-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.report-viewer-container.modal {
  background-color: rgba(0, 0, 0, 0.5);
  padding: 2rem;
}

.report-viewer-container.fullscreen {
  background-color: white;
  padding: 2px;
}

.report-viewer {
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
}

.report-viewer.fullscreen-content {
  width: 100%;
  height: 100%;
  border-radius: 0;
}

.modal .report-viewer {
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  padding: 1rem;
}

.report-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-button {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.2s ease;
}

.action-button:hover {
  background-color: #f5f5f5;
  color: #333;
  border-color: #ccc;
}

.icon {
  width: 1.25rem;
  height: 1.25rem;
}

.divider {
  width: 1px;
  height: 24px;
  background-color: #ddd;
  margin: 0 0.5rem;
}

.table-container {
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  padding: 0 1rem;
}

thead,
tfoot {
  position: sticky;
  width: fit-content;
  background: white;
  z-index: 10;
}

thead {
  top: 0;
}

tfoot {
  bottom: 0;
}

table {
  width: 100%;
  border-collapse: collapse;
  color: #333;
}

th,
td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

th {
  background-color: #f2f2f2;
  position: sticky;
  top: 0;
  z-index: 1;
}

tr.expandable {
  cursor: zoom-in;
}

tr.expandable:hover {
  background-color: #f5f5f5;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
}

.pagination-button {
  padding: 0.5rem 1rem;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
}

.pagination-button:disabled {
  background-color: #f5f5f5;
  color: #ccc;
  cursor: not-allowed;
  border-color: #eee;
}

.pagination-button:not(:disabled):hover {
  background-color: #f5f5f5;
  color: #333;
  border-color: #ccc;
}

.page-info {
  font-size: 0.9rem;
  color: #666;
}

.text-right {
  text-align: right;
}
</style>
